import type { Language } from "./languages";
import { isCuratedComparisonSlug } from "./curatedComparisons";

export const GUIDE_PATH = "/guides/ai-api-pricing";
export const COMPARE_PATH = "/compare";
export const CALCULATOR_PATH = "/calculators/llm-api-cost";

export function slugify(value: string) {
  return value
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 72) || "model";
}

function shortHash(value: string) {
  let hash = 0;
  for (let index = 0; index < value.length; index += 1) {
    hash = (Math.imul(hash, 31) + value.charCodeAt(index)) >>> 0;
  }
  return hash.toString(36).slice(0, 6);
}

export function createModelSlug(modelKey: string, modelName: string) {
  return `${slugify(modelName)}-${shortHash(modelKey)}`;
}

export function modelPath(providerId: string, slug: string) {
  return `/models/${encodeURIComponent(providerId)}/${encodeURIComponent(slug)}`;
}

export function providerPath(providerId?: string) {
  return providerId ? `/providers/${encodeURIComponent(providerId)}` : "/providers";
}

export function localizedPath(
  path: string,
  language: Language,
  values?: Record<string, string | number | undefined>,
) {
  const [pathAndQuery, hash = ""] = path.split("#");
  const [pathname, existingQuery = ""] = pathAndQuery.split("?");
  const params = new URLSearchParams(existingQuery);
  params.set("lang", language);
  if (values) {
    for (const [key, value] of Object.entries(values)) {
      if (value !== undefined && value !== "") params.set(key, String(value));
    }
  }
  const query = params.toString();
  return `${query ? `${pathname}?${query}` : pathname}${hash ? `#${hash}` : ""}`;
}

export type AppRoute =
  | { type: "home" }
  | { type: "providers" }
  | { type: "provider"; providerId: string }
  | { type: "model"; providerId: string; slug: string }
  | { type: "compare"; modelUids: string[] }
  | { type: "compare-directory" }
  | { type: "curated-compare"; slug: string }
  | { type: "calculator" }
  | { type: "guide" }
  | { type: "rankings" }
  | { type: "not-found" };

export function parseRoute(pathname: string, search = ""): AppRoute {
  let segments: string[];
  try {
    segments = pathname.split("/").filter(Boolean).map(decodeURIComponent);
  } catch {
    return { type: "not-found" };
  }
  if (segments.length === 0) return { type: "home" };
  if (segments.length === 1 && segments[0] === "rankings") return { type: "rankings" };
  if (segments.length === 1 && segments[0] === "providers") return { type: "providers" };
  if (segments.length === 2 && segments[0] === "providers") {
    return { type: "provider", providerId: segments[1] };
  }
  if (segments.length === 3 && segments[0] === "models") {
    return { type: "model", providerId: segments[1], slug: segments[2] };
  }
  if (segments.length === 1 && segments[0] === "compare") {
    const query = new URLSearchParams(search);
    if (!query.has("models")) return { type: "compare-directory" };
    const modelUids = (query.get("models") ?? "")
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean)
      .slice(0, 3);
    return { type: "compare", modelUids };
  }
  if (segments.length === 2 && segments[0] === "compare") {
    return isCuratedComparisonSlug(segments[1])
      ? { type: "curated-compare", slug: segments[1] }
      : { type: "not-found" };
  }
  if (pathname.replace(/\/$/, "") === CALCULATOR_PATH) return { type: "calculator" };
  if (pathname.replace(/\/$/, "") === GUIDE_PATH) return { type: "guide" };
  return { type: "not-found" };
}
